/**
 * Catalog lookups with the local model: for products you've bought, ask what they are, other
 * names people use for them, a fitting emoji, and whether they're usually taxed in California.
 * The model only knows what it learned in training (no web access), so its answers are marked
 * as AI suggestions and never replace anything you set yourself.
 */
import type { CatalogEntry } from "../engine/ledger.ts";
import type { CatalogNote } from "../types.ts";

export interface LookupItem {
  key: string;
  itemNo: string;
  name: string;
  store: string;
}

export interface LookupResult {
  key: string;
  note: CatalogNote;
}

export const LOOKUP_SYSTEM = `You label grocery and warehouse-club products for a shared shopping ledger in California.
For each product, reply with what it most likely is, judging from the store and the receipt abbreviation.
California sales tax: most food for home is NOT taxed (fruit, vegetables, meat, fish, bread, dairy, snacks, candy, nuts, non-carbonated juice and tea, coffee beans). TAXED: carbonated drinks and sparkling water, alcohol, hot prepared food, restaurant meals, and anything that isn't food (household, electronics, clothing, tools, paper goods, supplements are usually taxed).
Reply with JSON only: an array with one object per product, in the same order:
[{"key":"...","name":"The common name shoppers know it by, with the brand (like 'Kirkland Signature Bottled Water'), not the receipt abbreviation","keywords":["3 to 6 lowercase words people might search"],"icon":"one emoji","taxable":true|false|null}]
Use null for taxable when you can't tell. Don't add any other text.`;

export function lookupPrompt(items: readonly LookupItem[]): string {
  return items
    .map((i) =>
      JSON.stringify({
        key: i.key,
        store: i.store,
        item_no: i.itemNo || undefined,
        receipt_name: i.name,
      }),
    )
    .join("\n");
}

/** A note with something the lookup would fill in (a noted price alone doesn't count). */
export function hasLabels(n: CatalogNote): boolean {
  return Boolean(
    n.keywords.length || n.icon || n.note || (n.taxable !== undefined && n.taxable !== null),
  );
}

/** Products worth looking up: bought at least once, with no notes yet (or only AI notes when redoing). */
export function lookupQueue(
  catalog: readonly CatalogEntry[],
  notes: Record<string, CatalogNote> | undefined,
  redoAi = false,
): LookupItem[] {
  const out: LookupItem[] = [];
  for (const c of catalog) {
    const key = `#${c.itemNo}`;
    const n = notes?.[key];
    if (n && hasLabels(n) && (n.source !== "ai" || !redoAi)) continue;
    out.push({ key, itemNo: c.itemNo, name: c.name, store: c.store });
  }
  return out;
}

/**
 * Reads the model's reply. Tolerates code fences, chatter around the JSON, and partial
 * answers; keeps only well-formed entries for keys that were asked about.
 */
export function parseLookupReply(text: string, asked: readonly LookupItem[]): LookupResult[] {
  const start = text.indexOf("[");
  const end = text.lastIndexOf("]");
  if (start < 0 || end <= start) return [];
  let rows: unknown;
  try {
    rows = JSON.parse(text.slice(start, end + 1));
  } catch {
    return [];
  }
  if (!Array.isArray(rows)) return [];
  const keys = new Set(asked.map((a) => a.key));
  const out: LookupResult[] = [];
  rows.forEach((r, i) => {
    if (!r || typeof r !== "object") return;
    const row = r as Record<string, unknown>;
    const key = typeof row.key === "string" && keys.has(row.key) ? row.key : asked[i]?.key;
    if (!key || out.some((o) => o.key === key)) return;
    const keywords = Array.isArray(row.keywords)
      ? [
          ...new Set(
            row.keywords
              .filter((k): k is string => typeof k === "string")
              .map((k) => k.trim().toLowerCase())
              .filter((k) => k && k.length <= 40),
          ),
        ].slice(0, 8)
      : [];
    const icon =
      typeof row.icon === "string" && row.icon.trim() && [...row.icon.trim()].length <= 4
        ? row.icon.trim()
        : undefined;
    const taxable = row.taxable === true ? true : row.taxable === false ? false : null;
    const name = typeof row.name === "string" ? row.name.trim().slice(0, 120) : "";
    const note: CatalogNote = { keywords, taxable, source: "ai" };
    if (icon) note.icon = icon;
    if (name) note.note = name;
    out.push({ key, note });
  });
  return out;
}

const plain = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The name people know a product by, from its catalog note (the lookup's readable name), to show
 * in brackets after the receipt's name. Empty when there's none or it says the same thing.
 */
export function commonName(receiptName: string, note: CatalogNote | undefined): string {
  const n = note?.note?.trim() ?? "";
  return n && plain(n) !== plain(receiptName) ? n : "";
}
